import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, Calendar, Sunrise, Sun, Moon, Utensils } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const userEmail = localStorage.getItem("userEmail");

  useEffect(() => {
    const fetchHistory = async () => {
      if (!userEmail) { setLoading(false); return; }
      try {
        setLoading(true);
        const response = await axios.get(`${API}/history/${userEmail}`);
        setHistory(response.data);
      } catch (e) {
        console.error("Error fetching history:", e);
        toast.error("Couldn't load history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [userEmail]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateStr === today.toISOString().split('T')[0]) return "Today";
    if (dateStr === yesterday.toISOString().split('T')[0]) return "Yesterday";
    return date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  };

  const getMealIcon = (mealType) => {
    switch(mealType?.toLowerCase()) {
      case 'breakfast': return <Sunrise size={16} className="text-amber-600" />;
      case 'lunch': return <Sun size={16} className="text-orange-600" />;
      case 'dinner': return <Moon size={16} className="text-indigo-600" />;
      default: return <Utensils size={16} className="text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="app-container py-16 flex flex-col items-center justify-center min-h-[60vh]" data-testid="history-loading">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 animate-pulse">
          <History size={32} className="text-primary" strokeWidth={1.5} />
        </div>
        <p className="text-muted-foreground animate-fade-in">Loading your meal history...</p>
      </div>
    );
  }

  if (!userEmail) {
    return (
      <div className="app-container py-16 flex flex-col items-center justify-center min-h-[60vh]" data-testid="history-no-user">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-6">
          <History size={32} className="text-muted-foreground" strokeWidth={1.5} />
        </div>
        <h2 className="heading-section text-xl mb-2">Please login first</h2>
        <p className="text-muted-foreground">Your meal history will appear here</p>
      </div>
    );
  }

  return (
    <div className="app-container py-8 md:py-12" data-testid="history-page">
      <section className="mb-8 md:mb-12 animate-fade-in-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <History size={24} className="text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="heading-display text-3xl md:text-4xl">Meal History</h1>
            <p className="text-muted-foreground text-sm">Your past meal selections</p>
          </div>
        </div>
      </section>

      {history.length === 0 ? (
        <div className="text-center py-16 animate-fade-in-up" data-testid="history-empty">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
            <Calendar size={36} className="text-muted-foreground" strokeWidth={1.5} />
          </div>
          <h2 className="heading-section text-xl mb-2">No meals yet</h2>
          <p className="text-muted-foreground max-w-sm mx-auto">
            Start selecting meals for your day and they'll appear here as your history.
          </p>
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in-up">
          {history.map((day, index) => (
            <div key={day.date} className="bg-white rounded-3xl border border-border/40 overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}>
              <div className="bg-muted/30 px-6 py-4 border-b border-border/40">
                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-primary" />
                  <h3 className="font-semibold text-foreground">{formatDate(day.date)}</h3>
                  <span className="text-xs text-muted-foreground ml-auto">{day.date}</span>
                </div>
              </div>
              <div className="divide-y divide-border/40">
                {day.meals
                  .sort((a, b) => {
                    const order = { breakfast: 1, lunch: 2, dinner: 3 };
                    return (order[a.meal_type] || 4) - (order[b.meal_type] || 4);
                  })
                  .map((meal) => (
                    <div key={meal.id} className="flex items-center gap-4 p-4 hover:bg-muted/20 transition-colors">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-muted shrink-0">
                        <img src={meal.meal_image || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200"}
                          alt={meal.meal_name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getMealIcon(meal.meal_type)}
                          <span className="text-xs text-muted-foreground capitalize">{meal.meal_type}</span>
                        </div>
                        <p className="font-medium text-foreground truncate">{meal.meal_name}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
